/**
 * Status announcements shown to users in the dashboard.
 *
 * ────────────────────────────────────────────────────────────────────────
 * TO PUBLISH A MESSAGE: add an entry to the ANNOUNCEMENTS array below and
 * ship a frontend deploy. To retract one early, remove it (or set `expiresAt`
 * to the past). Everything else — dismiss-forever, expiry, targeting — is
 * handled for you.
 * ────────────────────────────────────────────────────────────────────────
 *
 * Each announcement:
 *   id         string    Stable, unique. Used as the dismiss key — NEVER reuse
 *                        an id for a different message or dismissals leak across.
 *   type       string    'info' | 'success' | 'warning'  (controls color/icon)
 *   message    string    Keep it short and to the point. Plain text.
 *   expiresAt  string?   ISO date. Hidden for everyone once the clock passes it.
 *                        Omit for a message with no auto-expiry.
 *   dismissible boolean? Default true. If false, no dismiss button (use sparingly).
 *   targeting  object?   All conditions must pass for the message to show:
 *                          minTasks number?   user must have >= this many tasks
 *                          plans    string[]? only these plan names (e.g. ['free'])
 *                          views    string[]? only on these dashboard views
 */
export const ANNOUNCEMENTS = [
  {
    id: 'worker-incident-2026-07',
    type: 'success',
    message:
      'Service restored. We resolved an issue affecting task runs over the past couple of days. Everything is working normally again.',
    expiresAt: '2026-07-10T22:00:00Z',
    targeting: { minTasks: 2 },
  },
];

const DISMISS_PREFIX = 'announcement:dismissed:';

const dismissKey = (id) => `${DISMISS_PREFIX}${id}`;

export function isDismissed(id) {
  try {
    return localStorage.getItem(dismissKey(id)) === 'true';
  } catch {
    return false;
  }
}

export function dismissAnnouncement(id) {
  try {
    localStorage.setItem(dismissKey(id), 'true');
  } catch {
    /* localStorage unavailable — dismissal just won't persist */
  }
}

/**
 * Does an announcement pass its targeting rules for the given context?
 * @param {object} announcement
 * @param {{ totalTasks?: number, plan?: string|null, view?: string }} ctx
 */
function matchesTargeting(announcement, ctx = {}) {
  const t = announcement.targeting;
  if (!t) return true;

  if (typeof t.minTasks === 'number' && (ctx.totalTasks ?? 0) < t.minTasks) {
    return false;
  }
  if (Array.isArray(t.plans) && t.plans.length > 0) {
    const plan = ctx.plan || 'free';
    if (!t.plans.includes(plan)) return false;
  }
  if (Array.isArray(t.views) && t.views.length > 0) {
    if (!t.views.includes(ctx.view)) return false;
  }
  return true;
}

/**
 * Resolve the announcements that should currently be visible.
 *
 * This is the single seam between the UI and the data source. Today it reads
 * the static ANNOUNCEMENTS config; to move authoring to a backend later, swap
 * the source here (make it async and fetch) — the hook and components don't change.
 *
 * @param {{ totalTasks?: number, plan?: string|null, view?: string, now?: number }} ctx
 * @returns {Array} visible announcements, in config order
 */
export function getActiveAnnouncements(ctx = {}) {
  const now = ctx.now ?? Date.now();
  return ANNOUNCEMENTS.filter((a) => {
    if (a.expiresAt && now >= new Date(a.expiresAt).getTime()) return false;
    if (isDismissed(a.id)) return false;
    return matchesTargeting(a, ctx);
  });
}

/** Whether any announcement could ever require the task count (to avoid a needless fetch). */
export function anyAnnouncementNeedsTaskCount(now = Date.now()) {
  return ANNOUNCEMENTS.some(
    (a) =>
      typeof a.targeting?.minTasks === 'number' &&
      (!a.expiresAt || now < new Date(a.expiresAt).getTime()) &&
      !isDismissed(a.id)
  );
}
